import React, { useState, useEffect, useRef } from 'react';
import './PostAdPage.css';
import logo from '../assets/logo.png';
import MapModal from '../components/MapModal';
import { fetchPlaceSuggestions } from '../components/api';
import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, setDoc, arrayUnion } from 'firebase/firestore';
import { uploadMultipleImages } from '../cloudinaryService';
import PostAdSectionOne from './PostAdSectionOne';
import PostAdSectionTwo from './PostAdSectionTwo';
import AdResultModal from './AdResultModal';
import PrePublishTermsModal from './PrePublishTermsModal';
import imageCompression from 'browser-image-compression';
import { cities, regions } from '../constants/locations';
import { accommodationTypes } from '../constants/accommodationTypes';
import { amenitiesList } from '../constants/amenities';
import { policyOptions } from '../constants/policies';
import { tenantsOptions } from '../constants/tenants';
import { priceDurations } from '../constants/pricing';
import PostAdFeatureModal from '../components/PostAdFeatureModal';

// New: options for the "What type of host are you?" question
const hostTypeOptions = [
  { value: 'owner', ar: 'مالك للوحدة', en: 'Property Owner' },
  { value: 'tenant', ar: 'المستأجر الحالي', en: 'Current Tenant' },
  { value: 'manager', ar: 'مدير للوحدة', en: 'Managing the unit' },
];

function PostAdPage({ currentLanguage, getLocalizedText }) {
  // دالة بسيطة لاكتشاف لغة النص اللي المستخدم كاتبه
  // لو فيه حروف عربي تعتبر النص عربي، غير كده انجليزي
  const detectQueryLanguage = (text) => {
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text) ? 'ar' : 'en';
  };

  // Refactored: Dynamic amenities initialization
  const initialAmenities = amenitiesList.reduce((acc, amenity) => {
    acc[`amenities_${amenity.value}`] = false;
    return acc;
  }, {});

  const [formData, setFormData] = useState({
    host_type: '',
    host_manager_value: '',
    accommodation_type: '',
    price: '',
    price_type: '',
    insurance: '',
    price_includes_bills: false,
    ...initialAmenities, // Refactored: Dynamic amenities keys
    smoking_policy: '',
    visitors_policy: '',
    pets_policy: '',
    tenants_policy: '',
    cleaning_explanation: '',
    ad_description: '',
    ad_location: '',
    lat: null, 
    lng: null,
    ad_contact_phone: '',
    ad_contact_whatsapp: '',
    ad_images: [],
    city: '',
    region: '',
  });

  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);
  const citySelectorRef = useRef(null);
  const regionSelectorRef = useRef(null);
  const searchBarRef = useRef(null);
  const debounceTimeout = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adResult, setAdResult] = useState(null);
  const [showPrePublishModal, setShowPrePublishModal] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState(null);

  const [showPostAdFeatureModal, setShowPostAdFeatureModal] = useState(false);
  const [postAdFeatureType, setPostAdFeatureType] = useState(null);

  const [mapInitialPosition, setMapInitialPosition] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 992px)').matches;
    if (!isMobile) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        citySelectorRef.current &&
        !citySelectorRef.current.contains(event.target) &&
        regionSelectorRef.current &&
        !regionSelectorRef.current.contains(event.target) &&
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target)
      ) {
        setIsCityDropdownOpen(false);
        setIsRegionDropdownOpen(false);
        setIsSearchDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const compressImages = async (files) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    const MAX_INPUT_MB = 25;

    const total = files.length;
    setCompressionStatus({ current: 0, total });

    const compressed = [];
    const failedNames = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        if (!file.type || !file.type.startsWith('image/')) {
          throw new Error('not an image');
        }
        if (file.size > MAX_INPUT_MB * 1024 * 1024) {
          throw new Error('too large before compression');
        }

        const compressedFile = await imageCompression(file, options);

        if (compressedFile.size > 10 * 1024 * 1024) {
          throw new Error('still too large after compression');
        }
        compressed.push(compressedFile);
      } catch (error) {
        failedNames.push(file.name);
      } finally {
        setCompressionStatus({ current: i + 1, total });
      }
    }

    setCompressionStatus(null);

    if (failedNames.length > 0) {
      alert(getLocalizedText(
        `تعذر رفع ${failedNames.length} صورة (${failedNames.join(', ')})، تم رفع الباقي بنجاح`,
        `Couldn't process ${failedNames.length} image(s) (${failedNames.join(', ')}), the rest were added successfully`
      ));
    }

    return compressed;
  };

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    
    // Refactored: Handle dynamic amenities
    if (type === 'checkbox' && name.startsWith('amenities_')) {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'file' && files) {
      const newImages = Array.from(files).slice(0, 9 - formData.ad_images.length);
      if (newImages.length === 0) return;

      const compressedFiles = await compressImages(newImages);
      if (compressedFiles.length > 0) {
        setFormData(prevFormData => ({
          ...prevFormData,
          ad_images: [...prevFormData.ad_images, ...compressedFiles]
        }));
      }
    } else if (name === 'ad_contact_phone' || name === 'ad_contact_whatsapp') {
      if (/^\d*$/.test(value)) {
        setFormData(prevFormData => ({
          ...prevFormData,
          [name]: value
        }));
      }
    } else if (name === 'ad_location') {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value
      }));
      setErrorMessage('');
      if (value.length < 4) {
        setSuggestions([]);
        setIsSearchDropdownOpen(false);
        return;
      }
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(async () => {
        try {
          const queryLang = detectQueryLanguage(value);
          const results = await fetchPlaceSuggestions(value, formData.city || null, queryLang);
          setSuggestions(results.slice(0, 4));
          setIsSearchDropdownOpen(true);
        } catch (error) {
          setErrorMessage(getLocalizedText('خطأ في جلب الاقتراحات', 'Error fetching suggestions'));
          setSuggestions([]);
          setIsSearchDropdownOpen(true);
        }
      }, 500);
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleCitySelect = (city) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      city: city.value, // Refactored: use .value
      region: ''
    }));
    setIsCityDropdownOpen(false);
  };

  const handleRegionSelect = (region) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      region: region.value // Refactored: use .value
    }));
    setIsRegionDropdownOpen(false);
  };

  const handleRemoveImage = (index) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      ad_images: prevFormData.ad_images.filter((_, i) => i !== index)
    }));
  };

  // بتخلي الصورة اللي المستخدم اختارها هي صورة الكافر (أول صورة في المصفوفة)
  const handleSetCoverImage = (index) => {
    if (index === 0) return; // هي أصلاً الكافر
    setFormData(prevFormData => {
      const images = [...prevFormData.ad_images];
      const [selectedImage] = images.splice(index, 1);
      images.unshift(selectedImage);
      return {
        ...prevFormData,
        ad_images: images
      };
    });
  };

  const handleSuggestionSelect = (suggestion) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      ad_location: suggestion.properties.formatted,
      lat: suggestion.properties.lat,
      lng: suggestion.properties.lon 
    }));
    setIsSearchDropdownOpen(false);
  };

  const handleAddressClick = (e) => {
    e.stopPropagation();
    setIsSearchDropdownOpen(true);
  };

  const handleSelectLocation = ({ address, lat, lng }) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      ad_location: address,
      lat: lat,
      lng: lng
    }));
    setShowMapModal(false);
    setMapInitialPosition(null);
  };

  const handleSearchOptionSelect = (e) => {
    e.stopPropagation();
    setIsSearchDropdownOpen(false);
    setShowMapModal(true);
  };

  // Refactored: Updated isFormValid as per manager's logic
  const isFormValid = () => {
    const phoneRegex = /^\d{11}$/;
    const isStudio = formData.accommodation_type === 'studio';

    const isManager = formData.host_type === 'manager';

    return (
      formData.host_type &&
      (!isManager ||
        (formData.host_manager_value.length >= 75 && formData.host_manager_value.length <= 1500)) &&
      formData.accommodation_type &&
      formData.price &&
      formData.price_type &&
      formData.insurance &&
      (isStudio || formData.tenants_policy) &&     // ← الآن صحيح
      formData.smoking_policy &&
      formData.visitors_policy &&
      formData.pets_policy &&
      formData.cleaning_explanation.length >= 50 &&
      formData.cleaning_explanation.length <= 750 &&
      formData.ad_location &&
      phoneRegex.test(formData.ad_contact_phone) &&
      phoneRegex.test(formData.ad_contact_whatsapp) &&
      formData.ad_images.length >= 4 &&
      formData.city &&
      formData.region &&

      formData.ad_description.length >= 75 &&
      formData.ad_description.length <= 1500
    );
  };

// Helper function to check if a field is valid (for green check)
  const isFieldValid = (field) => {
    switch (field) {
      case 'host_type':
        return !!formData.host_type;
      case 'host_manager_value':
        return (
          formData.host_type !== 'manager' ||
          (formData.host_manager_value.length >= 75 && formData.host_manager_value.length <= 1500)
        );
      case 'cleaning_explanation':
        return formData.cleaning_explanation.length >= 50 && formData.cleaning_explanation.length <= 750;
      case 'accommodation_type':
        return !!formData.accommodation_type;
      case 'price':
        return !!formData.price && parseFloat(formData.price) > 0;
      case 'price_type':
        return !!formData.price_type;
      case 'insurance':
        return !!formData.insurance && parseFloat(formData.insurance) > 0;
      case 'tenants_policy':
        return formData.accommodation_type === 'studio' || !!formData.tenants_policy;
      case 'smoking_policy':
      case 'visitors_policy':
      case 'pets_policy':
        return !!formData[field];
        case 'region':
        return !!formData.region;
      case 'ad_location':
        return !!formData.ad_location && !!formData.lat && !!formData.lng;
      case 'ad_contact_phone':
      case 'ad_contact_whatsapp':
        const phoneRegex = /^\d{11}$/;
        return phoneRegex.test(formData[field]);
      case 'ad_images':
        return formData.ad_images.length >= 4;

      case 'ad_description':
        return formData.ad_description.length >= 75 && formData.ad_description.length <= 1500;
        
      default:
        return false;
    }
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (!auth.currentUser) {
      alert(getLocalizedText('يجب تسجيل الدخول أولاً', 'Please login first'));
      window.location.href = '/';
      return;
    }

    if (!isFormValid()) {
      alert(getLocalizedText('يرجى التأكد من ملء جميع الحقول ورفع ٤ صور على الأقل', 'Please ensure all fields are filled and at least 4 images are uploaded'));
      return;
    }

    if (!formData.lat || !formData.lng) {
        alert(getLocalizedText('يرجى تحديد الموقع بدقة من الخريطة أو القائمة', 'Please select a location accurately from the map or list'));
        return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const imageUrls = await uploadMultipleImages(formData.ad_images);
      
      // Refactored: Collect dynamic amenities for adData
      const amenities = amenitiesList.reduce((acc, amenity) => {
        acc[amenity.value] = formData[`amenities_${amenity.value}`];
        return acc;
      }, {});

      const adData = {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        host_type: formData.host_type,
        host_manager_value: formData.host_type === 'manager' ? formData.host_manager_value : '',
        accommodation_type: formData.accommodation_type,
        price: parseFloat(formData.price),
        price_type: formData.price_type,
        insurance: parseFloat(formData.insurance),
        price_includes_bills: formData.price_includes_bills,
        amenities, // Refactored: Dynamic amenities object
        smoking_policy: formData.smoking_policy,
        visitors_policy: formData.visitors_policy,
        pets_policy: formData.pets_policy,
        tenants_policy: formData.tenants_policy,
        cleaning_explanation: formData.cleaning_explanation,
        ad_description: formData.ad_description,
        ad_location: formData.ad_location,
        latitude: formData.lat,
        longitude: formData.lng,
        city: formData.city,
        region: formData.region,
        ad_contact_phone: formData.ad_contact_phone,
        ad_contact_whatsapp: formData.ad_contact_whatsapp,
        ad_images: imageUrls,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
      };

      const docRef = await addDoc(collection(db, 'ads'), adData);
      const newAdId = docRef.id;

      if (auth.currentUser) {
        const userDocRef = doc(db, 'verified_users', auth.currentUser.uid);
        await setDoc(userDocRef, {
          posted_ads_id: arrayUnion(newAdId),      
          favourite_ads_id: arrayUnion()           
        }, { merge: true });
      }

      setAdResult({ success: true });
    } catch (error) {
      console.error('❌ Error publishing ad:', error);
      setAdResult({
        success: false,
        message: error.message || 'حدث خطأ غير معروف'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseMapModal = () => {
    setShowMapModal(false);
    setMapInitialPosition(null);
  };

  return (
    <div className="post-ad-page">
      <div className="post-ad-form-container">
        <div className="form-header">
          <img src={logo} alt="ROOMATE Logo" className="form-header-logo" />
          <h2>{getLocalizedText('نشر إعلان جديد', 'Post a New Ad')}</h2>
          <span className="close-button" onClick={() => (window.location.href = '/')}>×</span>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="ad-form-content">
          <div className="form-body">
            
            {/* Section One: Refactored with dynamic props */}
            <PostAdSectionOne
              formData={formData}
              handleChange={handleChange}
              handleRemoveImage={handleRemoveImage}
              handleSetCoverImage={handleSetCoverImage}
              getLocalizedText={getLocalizedText}
              accommodationTypes={accommodationTypes}
              tenantsOptions={tenantsOptions}
              priceDurations={priceDurations}
              hostTypeOptions={hostTypeOptions}
              isFieldValid={isFieldValid}
            />

            {/* Section Two: Refactored with dynamic props */}
            <PostAdSectionTwo
              formData={formData}
              handleChange={handleChange}
              getLocalizedText={getLocalizedText}
              currentLanguage={currentLanguage}
              cities={cities}
              allRegions={regions} // Refactored: Use imported regions
              citySelectorRef={citySelectorRef}
              isCityDropdownOpen={isCityDropdownOpen}
              setIsCityDropdownOpen={setIsCityDropdownOpen}
              handleCitySelect={handleCitySelect}
              regionSelectorRef={regionSelectorRef}
              setShowTooltip={setShowTooltip}
              showTooltip={showTooltip}
              isRegionDropdownOpen={isRegionDropdownOpen}
              setIsRegionDropdownOpen={setIsRegionDropdownOpen}
              handleRegionSelect={handleRegionSelect}
              searchBarRef={searchBarRef}
              handleAddressClick={handleAddressClick}
              isSearchDropdownOpen={isSearchDropdownOpen}
              handleSearchOptionSelect={handleSearchOptionSelect}
              errorMessage={errorMessage}
              suggestions={suggestions}
              handleSuggestionSelect={handleSuggestionSelect}
              amenitiesList={amenitiesList}
              policyOptions={policyOptions}
              showPostAdFeatureModal={showPostAdFeatureModal}
              setShowPostAdFeatureModal={setShowPostAdFeatureModal}
              postAdFeatureType={postAdFeatureType}
              setPostAdFeatureType={setPostAdFeatureType}
              setIsSearchDropdownOpen={setIsSearchDropdownOpen}
              isFieldValid={isFieldValid}
            />

          </div>

          {compressionStatus && (
            <div className="compression-progress">
              {getLocalizedText('جاري ضغط الصور...', 'Compressing images...')} {compressionStatus.current}/{compressionStatus.total}
            </div>
          )}

          <div className="form-bottom-row">
            <button
              type="button"
              className="post-ad-button"
              disabled={!isFormValid() || isSubmitting || !!compressionStatus}
              style={{
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'wait' : 'pointer'
              }}
              onClick={() => setShowPrePublishModal(true)}
            >
              {isSubmitting 
                ? getLocalizedText('جاري النشر...', 'Publishing...') 
                : getLocalizedText('نشر الإعلان', 'Post Ad')
              }
            </button>
          </div>
        </form>
      </div>
      {showMapModal && (
        <MapModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={handleCloseMapModal}
          onSelectLocation={handleSelectLocation}
          initialPosition={mapInitialPosition}
        />
      )}

      {showPrePublishModal && (
        <PrePublishTermsModal
          isOpen={showPrePublishModal}
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          isSubmitting={isSubmitting}
          onClose={() => setShowPrePublishModal(false)}
          onConfirm={() => {
            setShowPrePublishModal(false);
            handleSubmit();
          }}
        />
      )}

      {adResult && (
        <AdResultModal
          isSuccess={adResult.success}
          message={adResult.message}
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => {
            const wasSuccess = adResult.success;
            setAdResult(null);
            if (wasSuccess) {
              window.location.href = '/';
            }
          }}
        />
      )}

     {showPostAdFeatureModal && (
       <PostAdFeatureModal
         isOpen={showPostAdFeatureModal}
         onClose={() => {
           setShowPostAdFeatureModal(false);
           setPostAdFeatureType(null);
         }}
         type={postAdFeatureType}
         currentLanguage={currentLanguage}
         getLocalizedText={getLocalizedText}
         onSelect={(selectedItem) => {
      // حفظ الإحداثيات + فتح الخريطة
      setMapInitialPosition({
        lat: selectedItem.lat,
        lng: selectedItem.lng,
        address: getLocalizedText(selectedItem.ar, selectedItem.en)
      });

      setShowMapModal(true);           // ← افتح الخريطة
      setShowPostAdFeatureModal(false);
    }}
  />
)}
    </div>
  );
}

export default PostAdPage;